import { Box, Tooltip, Typography, styled } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { ArrowDown, ArrowUp } from 'lucide-react';
import React, { useId, useMemo } from 'react';

import DataLabDataGrid from 'views/playground/features/data-lab/components/data-lab-data-grid';
import ChartCard from 'components/charts/chart-card';
import SparklineChart from 'components/charts/sparkline-chart';
import { useChartPalette } from 'components/charts/hooks/use-chart-palette';
import DataLab from 'views/playground/features/data-lab/components/data-lab';
import { ChartPalette, ChartStatus } from 'components/charts/types/chart-palette.type';
import {
  EmbeddedGridRow,
  WeightClassSegment,
  deriveEmbeddedGridRows,
} from 'views/playground/features/data-lab/utils/derive-chart-data';
import { useMetricFormatters } from 'views/playground/features/data-lab/hooks/use-metric-formatters';
import { HealthStatus } from 'views/playground/features/data-lab/types/health-status.type';
import { HEALTH_STATUS_COLOR, UTILIZATION_THRESHOLDS } from 'views/playground/features/data-lab/utils/health-status';

// Derived once at module scope: every row is every cage, unfiltered.
const embeddedGridRows = deriveEmbeddedGridRows();

const STATUS_LABELS: Record<HealthStatus, string> = {
  good: 'Good',
  watch: 'Watch',
  critical: 'Critical',
};

const HeaderRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  marginBottom: theme.spacing(6),
}));

const PageTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 24,
  fontWeight: 700,
  color: chartPalette.text.primary,
}));

// A 0–100 user-space rectangle: the gradient below is
// defined in these exact units via `gradientUnits="userSpaceOnUse"`, and
// `preserveAspectRatio="none"` stretches it to whatever pixel width the cell
// actually has, so the percent value maps directly onto the gradient's own
// coordinate system regardless of rendered size.
const UTILIZATION_VIEW_WIDTH = 100;
const UTILIZATION_VIEW_HEIGHT = 6;

const UtilizationSvg = styled('svg')({
  display: 'block',
  flexGrow: 1,
  minWidth: 0,
  width: '100%',
  height: UTILIZATION_VIEW_HEIGHT,
});

// Share one unique gradient across rows. `userSpaceOnUse` maps every bar to a
// common 0–100 range; object-bounding-box gradients would paint the full ramp
// in every cell. Two stops at each threshold keep status bands distinct.
const UtilizationGradientDefs: React.FC<{ id: string; statusColors: ChartStatus }> = ({ id, statusColors }) => {
  const { watch, critical } = UTILIZATION_THRESHOLDS;

  return (
    <svg width={0} height={0} style={{ position: 'absolute' }} aria-hidden="true">
      <defs>
        <linearGradient id={id} gradientUnits="userSpaceOnUse" x1={0} y1={0} x2={UTILIZATION_VIEW_WIDTH} y2={0}>
          <stop offset="0%" stopColor={statusColors[HEALTH_STATUS_COLOR.good]} />
          <stop offset={`${watch}%`} stopColor={statusColors[HEALTH_STATUS_COLOR.good]} />
          <stop offset={`${watch}%`} stopColor={statusColors[HEALTH_STATUS_COLOR.watch]} />
          <stop offset={`${critical}%`} stopColor={statusColors[HEALTH_STATUS_COLOR.watch]} />
          <stop offset={`${critical}%`} stopColor={statusColors[HEALTH_STATUS_COLOR.critical]} />
          <stop offset="100%" stopColor={statusColors[HEALTH_STATUS_COLOR.critical]} />
        </linearGradient>
      </defs>
    </svg>
  );
};

const CellValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'chartPalette',
})<{ chartPalette: ChartPalette }>(({ chartPalette }) => ({
  fontSize: 13,
  fontVariantNumeric: 'tabular-nums',
  color: chartPalette.text.primary,
}));

// Fills the cell and centers short custom content. Plain-text columns need no
// wrapper because the grid centers a single line of text itself.
const CellContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  height: '100%',
});

// Use a slim status-scale bar and keep the percentage beside it so color is
// never the only status cue.
const UtilizationCell: React.FC<{ percent: number; gradientId: string }> = ({ percent, gradientId }) => {
  const { formatPercent } = useMetricFormatters();
  const palette = useChartPalette();
  const formatted = formatPercent(percent, 0);
  const clampedPercent = Math.min(100, Math.max(0, percent));

  return (
    <Box display="flex" alignItems="center" gap={2} role="img" aria-label={`${formatted} utilized`}>
      <UtilizationSvg viewBox={`0 0 ${UTILIZATION_VIEW_WIDTH} ${UTILIZATION_VIEW_HEIGHT}`} preserveAspectRatio="none">
        <rect width={UTILIZATION_VIEW_WIDTH} height={UTILIZATION_VIEW_HEIGHT} rx={3} fill={palette.backgrounds.sunken} />
        <rect width={clampedPercent} height={UTILIZATION_VIEW_HEIGHT} rx={3} fill={`url(#${gradientId})`} />
      </UtilizationSvg>
      <CellValue chartPalette={palette}>{formatted}</CellValue>
    </Box>
  );
};

const StatusDot = styled('span', { shouldForwardProp: (prop) => prop !== 'dotColor' })<{ dotColor: string }>(({ dotColor }) => ({
  width: 8,
  height: 8,
  borderRadius: '50%',
  backgroundColor: dotColor,
  flexShrink: 0,
  display: 'inline-block',
}));

// Health column: an 8px status dot plus label, using the status scale (spec
// 9.5). Same mortality-rate bands `derive-chart-data.ts` uses everywhere else.
const HealthCell: React.FC<{ status: HealthStatus }> = ({ status }) => {
  const palette = useChartPalette();
  const label = STATUS_LABELS[status];

  return (
    <Box display="flex" alignItems="center" gap={2} role="img" aria-label={`Health status: ${label}`}>
      <StatusDot dotColor={palette.status[HEALTH_STATUS_COLOR[status]]} />
      <CellValue chartPalette={palette}>{label}</CellValue>
    </Box>
  );
};

const DistributionTrack = styled(Box)({
  display: 'flex',
  width: '100%',
  height: 8,
  borderRadius: 4,
  overflow: 'hidden',
});

const DistributionSegment = styled(Box, { shouldForwardProp: (prop) => prop !== 'segmentColor' && prop !== 'flexValue' })<{
  segmentColor: string;
  flexValue: number;
}>(({ segmentColor, flexValue }) => ({
  flexGrow: flexValue,
  backgroundColor: segmentColor,
}));

// Each segment represents a weight class derived from fixture average weight.
// Its tooltip names the segment and percentage.
const DistributionCell: React.FC<{ segments: WeightClassSegment[] }> = ({ segments }) => {
  const { formatPercent } = useMetricFormatters();
  const palette = useChartPalette();

  return (
    <DistributionTrack role="img" aria-label="Weight class distribution">
      {segments.map((segment, index) => (
        <Tooltip key={segment.label} title={`${segment.label}: ${formatPercent(segment.value, 0)}`}>
          <DistributionSegment segmentColor={palette.series.categorical[index % palette.series.categorical.length]} flexValue={segment.value} />
        </Tooltip>
      ))}
    </DistributionTrack>
  );
};

const DeltaValue = styled(Box, { shouldForwardProp: (prop) => prop !== 'deltaColor' })<{ deltaColor: string }>(({ deltaColor }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  fontSize: 13,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  color: deltaColor,
}));

// Biomass growth is good, matching the Overview's KPI deltas.
const DeltaCell: React.FC<{ percent: number }> = ({ percent }) => {
  const { formatPercent } = useMetricFormatters();
  const palette = useChartPalette();
  const direction = percent >= 0 ? 'up' : 'down';
  const color = percent === 0 ? palette.text.secondary : direction === 'up' ? palette.status.green : palette.status.red;
  const Icon = direction === 'up' ? ArrowUp : ArrowDown;
  const formatted = formatPercent(Math.abs(percent), 1);

  return (
    <DeltaValue deltaColor={color} role="img" aria-label={`${formatted} change over the last 12 weeks`}>
      <Icon size={14} />
      {formatted}
    </DeltaValue>
  );
};

// The chart fills the cell and is aria-hidden. The wrapping cell provides its
// accessible name.
const TrendCell: React.FC<{ trend: number[] }> = ({ trend }) => {
  const { formatCount } = useMetricFormatters();
  const endValue = trend[trend.length - 1] ?? 0;

  return (
    <Box width="100%" role="img" aria-label={`Biomass trend, ending at ${formatCount(endValue)} kilograms`}>
      <SparklineChart data={trend} height={24} disableKeyboardNavigation />
    </Box>
  );
};

// One row per cage, with columns for the sparkline and status summaries.
export const EmbeddedScreenBody: React.FC = () => {
  const palette = useChartPalette();
  const utilizationGradientId = useId().replace(/:/g, '');

  const columns: GridColDef<EmbeddedGridRow>[] = useMemo(
    () => [
      {
        field: 'cageName',
        headerName: 'Cage',
        flex: 1.2,
        align: 'left',
        headerAlign: 'left',
      },
      {
        field: 'trend',
        headerName: 'Trend',
        flex: 1,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <CellContent>
            <TrendCell trend={params.row.trend} />
          </CellContent>
        ),
      },
      {
        field: 'utilizationPercent',
        headerName: 'Utilization',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <CellContent>
            <UtilizationCell percent={params.row.utilizationPercent} gradientId={utilizationGradientId} />
          </CellContent>
        ),
      },
      {
        field: 'health',
        headerName: 'Health',
        flex: 1,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <CellContent>
            <HealthCell status={params.row.health} />
          </CellContent>
        ),
      },
      {
        field: 'distribution',
        headerName: 'Distribution',
        flex: 1,
        sortable: false,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => (
          <CellContent>
            <DistributionCell segments={params.row.distribution} />
          </CellContent>
        ),
      },
      {
        field: 'deltaPercent',
        headerName: 'Delta',
        flex: 0.8,
        align: 'left',
        headerAlign: 'left',
        renderCell: (params) => <DeltaCell percent={params.row.deltaPercent} />,
      },
    ],
    [utilizationGradientId]
  );

  return (
    <>
      <HeaderRow>
        <PageTitle chartPalette={palette} variant="h2">
          Embedded visualisations
        </PageTitle>
      </HeaderRow>

      <UtilizationGradientDefs id={utilizationGradientId} statusColors={palette.status} />

      <ChartCard title="Cage summary" subtitle="Every cage, one row, five visual cell types">
        <DataLabDataGrid
          chartPalette={palette}
          autoHeight
          hideFooter
          rows={embeddedGridRows}
          columns={columns}
          // Assign row parity classes for the scoped grid styling.
          getRowClassName={(params) => (params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd')}
        />
      </ChartCard>
    </>
  );
};

const EmbeddedScreen: React.FC = () => (
  <DataLab>
    <EmbeddedScreenBody />
  </DataLab>
);

export default EmbeddedScreen;
